import { FaCog, FaArrowUp } from 'react-icons/fa';

// Constants
const PRIORITY_OPTIONS = ['Low', 'Medium', 'High', 'Critical'];
const STATUSES = ['Open', 'On-hold', 'Pending', 'Solved', 'Cancelled', 'Closed'];
const STATUS_COLORS = {
    Open: '#1976d2',
    'On-hold': '#ff9800',
    Pending: '#fbc02d',
    Solved: '#388e3c',
    Cancelled: '#d32f2f',
    Closed: '#6E00B3'
};

/**
 * StatusPrioritySection Component
 * Renders the status, priority, and escalation sections in the sidebar.
 * 
 * @param {Object} props - Component props
 * @param {string} props.status - Current status
 * @param {Function} props.onStatusChange - Handler for status change
 * @param {boolean} props.showStatusSelect - Whether to show status dropdown
 * @param {Function} props.onToggleStatusSelect - Toggle status dropdown visibility
 * @param {string} props.priority - Current priority
 * @param {Function} props.onPriorityChange - Handler for priority change
 * @param {boolean} props.showPrioritySelect - Whether to show priority dropdown
 * @param {Function} props.onTogglePrioritySelect - Toggle priority dropdown visibility
 * @param {boolean} props.isEscalated - Whether item is escalated
 * @param {number} props.escalatedToUserID - ID of user escalated to
 * @param {string} props.escalationReason - Reason for escalation
 * @param {Function} props.onEscalate - Handler to open escalation modal
 * @param {Function} props.onDeEscalate - Handler to remove escalation
 * @param {Object} props.assignedUser - Currently assigned user
 * @param {Function} props.getUserName - Function to get user name by ID
 */
const StatusPrioritySection = ({
    status,
    onStatusChange,
    showStatusSelect,
    onToggleStatusSelect,
    priority,
    onPriorityChange,
    showPrioritySelect,
    onTogglePrioritySelect,
    isEscalated,
    escalatedToUserID,
    escalationReason,
    onEscalate,
    onDeEscalate,
    assignedUser,
    getUserName
}) => {
    return (
        <>
            {/* ================================================ */}
            {/* Escalation Section */}
            {/* ================================================ */}
            <div className='sidebar-label-row space-above sidebar-label-static'>
                <h4><span className='sidebar-label'>Escalation:</span></h4>
            </div>
            
            {isEscalated ? (
                <div className='escalation-status'>
                    <div className='escalation-badge escalated'>
                        <FaArrowUp size={12} />
                        <span>Escalated</span>
                    </div>
                    <p className='escalation-info'>
                        Escalated to: {getUserName(escalatedToUserID)}
                    </p>
                    {escalationReason && (
                        <p className='escalation-reason'>
                            Reason: {escalationReason}
                        </p>
                    )}
                    <button
                        className='de-escalate-btn'
                        onClick={onDeEscalate}
                    >
                        Remove Escalation
                    </button>
                </div>
            ) : (
                <div className='sidebar-value'>
                    <button
                        className='escalate-btn'
                        onClick={onEscalate}
                        disabled={!assignedUser}
                        title={!assignedUser ? 'Assign a user first to escalate' : 'Escalate to assigned user\'s manager'}
                    >
                        <FaArrowUp size={14} />
                        Escalate to Manager
                    </button>
                    {!assignedUser && (
                        <p style={{ fontSize: '0.7rem', color: '#a0aec0', marginTop: '4px' }}>
                            Assign a user first to enable escalation.
                        </p>
                    )}
                </div>
            )}

            {/* ================================================ */}
            {/* Priority Section */}
            {/* ================================================ */}
            <div className='sidebar-label-row space-above'>
                <h4><span className='sidebar-label'>Priority:</span></h4>
                <span
                    className='sidebar-gear'
                    title='Change priority'
                    onClick={(e) => {
                        e.stopPropagation();
                        onTogglePrioritySelect();
                    }}
                    style={{ cursor: 'pointer' }}
                >
                    <FaCog size={20} />
                </span>
            </div>

            {!showPrioritySelect && (
                <div className='sidebar-value'>
                    <span className='sidebar-priority'>{priority}</span>
                </div>
            )}

            {showPrioritySelect && (
                <select 
                    className='sidebar-priority-select' 
                    value={priority} 
                    onChange={onPriorityChange}
                >
                    {PRIORITY_OPTIONS.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                    ))}
                </select>
            )}

            {/* ================================================ */}
            {/* Status Section */}
            {/* ================================================ */}
            <div className='sidebar-label-row'>
                <h4><span className='sidebar-label'>Status:</span></h4>
                <span
                    className='sidebar-gear'
                    title='Change status'
                    onClick={(e) => {
                        e.stopPropagation();
                        onToggleStatusSelect();
                    }}
                    style={{ cursor: 'pointer' }}
                >
                    <FaCog size={20} />
                </span>
            </div>

            {!showStatusSelect && (
                <div className='sidebar-value'>
                    <span
                        className='status-circle'
                        style={{
                            backgroundColor: STATUS_COLORS[status] || '#ccc',
                            display: 'inline-block',
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            marginRight: '0.5em',
                            verticalAlign: 'middle'
                        }}
                    />
                    <span className='sidebar-status'>{status}</span>
                </div>
            )}

            {showStatusSelect && (
                <select 
                    className='sidebar-status-select' 
                    value={status} 
                    onChange={onStatusChange}
                >
                    {STATUSES.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                    ))}
                </select>
            )}
        </>
    );
};

export default StatusPrioritySection;
